import papaparse from 'papaparse';
import { queryString, removeEmpty } from '../../utils/formatters';
import request from '../../utils/request';

/**
 * Get a presigned URL for file upload
 * @returns {Promise<{pre_signed_url: string, key: string}>} Presigned URL and key
 */
const getPresignedUrl = async () => {
  const url = '/pricing-groups/document-signed-url';
  const response = await request().get(url);
  return response.data.data;
};

/**
 * Upload file to S3 using presigned URL
 * @param {string} presignedUrl The presigned URL for upload
 * @param {File} file The file to upload
 * @returns {Promise<void>}
 */
const uploadToPresignedUrl = async (presignedUrl, file) => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'text/csv'
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file: ${response.status} ${response.statusText}`);
  }
};

const getPricingGroups = async ({ page = 1, size = 50, name = '', startDate = [] }) => {
  const params = removeEmpty({
    page,
    size,
    name,
    startDate: startDate.length
      ? [startDate[0].format('YYYY-MM-DDT00:00:00'), startDate[1].format('YYYY-MM-DDT23:59:59')]
      : null
  });

  const queryParams = queryString(params);
  const url = `/pricing-groups/manage?${queryParams}`;
  const response = await request().get(url);
  return response.data.data;
};

const addPricingGroup = async (body) => {
  // Get presigned URL and key
  const { pre_signed_url, key } = await getPresignedUrl();

  // Upload file to presigned URL
  await uploadToPresignedUrl(pre_signed_url, body.file);

  // Update body with the key instead of file
  body.file = key;

  const url = '/pricing-groups/manage';
  const res = await request().post(url, body);
  return res.data.data;
};

const editPricingGroup = async (name, body) => {
  if (body.file) {
    // Get presigned URL and key
    const { pre_signed_url, key } = await getPresignedUrl();

    // Upload file to presigned URL
    await uploadToPresignedUrl(pre_signed_url, body.file);

    // Update body with the key instead of file
    body.file = key;
  }

  const url = `/pricing-groups/manage/${name}`;
  const res = await request().put(url, body);
  return res.data.data;
};

const approvePricingGroup = async (name) => {
  const url = `/pricing-groups/manage/${name}/approve`;
  const res = await request().patch(url);
  return res.data.data;
};

const rejectPricingGroup = async (name) => {
  const url = `/pricing-groups/manage/${name}/reject`;
  const res = await request().patch(url);
  return res.data.data;
};

// eslint-disable-next-line no-unused-vars
const getFileChunk = async (file) => {
  const opt = { type: 'text/csv;charset=utf-8;' };
  const files = [];

  return new Promise((resolve, reject) => {
    papaparse.parse(file, {
      header: true,
      fastMode: true,
      skipEmptyLines: true,
      chunkSize: 1024 * 1024 * 1, //  split to 1 mb
      chunk: (chunk) => {
        const data = papaparse.unparse(chunk.data);
        const blob = new Blob([data], opt);
        const file = new File([blob], 'file.csv', opt);
        files.push(file);
      },
      complete: () => resolve(files),
      error: (err) => reject(err)
    });
  });
};

export default {
  getPricingGroups,
  addPricingGroup,
  editPricingGroup,
  approvePricingGroup,
  rejectPricingGroup
};
